import * as React from "react";
import { MenuItem, Select, TextField } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import type { DeviceStorageInput } from "../../../lib/apis/dtos/request/device.request";
import { deviceErrorMessage, invalidateDeviceQueries } from "../../../lib/apis/device-cache";
import { upsertDeviceStorage } from "../../../lib/apis/v1/device.service";
import { getQueryClient } from "../../../config/queryClient.config";
import type { ModalComponentProps } from "../../../types/modal.types";
import FailSnackbar from "../../ui/snackbar/fail.component";
import SuccessSnackbar from "../../ui/snackbar/success.component";
import ConfirmModal from "../confirm.modal";

interface DeviceStorageModalProps extends ModalComponentProps {
  deviceId: string | number;
  storage?: DeviceStorageInput;
}

const MAX_PRICE = 2147483647;
const STORAGE_UNITS = ["GB", "TB"] as const;
type StorageUnit = typeof STORAGE_UNITS[number];

const parseStorage = (storage = "") => {
  const match = storage.trim().match(/^(\d+)\s*(GB|TB)$/i);
  return {
    amount: match?.[1] ?? storage.replace(/\D/g, ""),
    unit: (match?.[2]?.toUpperCase() ?? "GB") as StorageUnit,
  };
};

export default function DeviceStorageModal({
  id,
  onClose,
  deviceId,
  storage: initialStorage,
}: DeviceStorageModalProps) {
  const initialStorageValue = parseStorage(initialStorage?.storage);
  const [storageAmount, setStorageAmount] = React.useState(initialStorageValue.amount);
  const [storageUnit, setStorageUnit] = React.useState<StorageUnit>(initialStorageValue.unit);
  const [price, setPrice] = React.useState(initialStorage?.price?.toString() ?? "");
  const numericStorageAmount = Number(storageAmount);
  const invalidStorage = storageAmount === ""
    || !Number.isInteger(numericStorageAmount)
    || numericStorageAmount <= 0;
  const normalizedStorage = `${storageAmount}${storageUnit}`;
  const numericPrice = Number(price);
  const invalidPrice = price === ""
    || !Number.isInteger(numericPrice)
    || numericPrice < 0
    || numericPrice > MAX_PRICE;

  const mutation = useMutation({
    mutationFn: () => upsertDeviceStorage(deviceId, {
      storage: normalizedStorage,
      price: numericPrice,
    }),
    onSuccess: async () => {
      await invalidateDeviceQueries(getQueryClient());
      SuccessSnackbar(initialStorage ? "용량 출고가 수정 완료" : "용량 추가 완료");
      onClose?.();
    },
    onError: (error) => FailSnackbar(deviceErrorMessage(error)),
  });

  return (
    <ConfirmModal
      id={id}
      onClose={onClose}
      title={initialStorage ? "용량 출고가 수정" : "용량 추가"}
      className="w-[520px]"
      rightButton={{
        title: initialStorage ? "수정" : "추가",
        disabled: mutation.isPending || invalidStorage || invalidPrice,
        onClick: () => mutation.mutate(),
      }}
    >
      <div className="mt-4 flex flex-col gap-4">
        <div>
          <p className="mb-2 text-[13px] text-black/60">용량*</p>
          <div className="flex">
            <TextField
              className="flex-1"
              type="number"
              placeholder="예: 256"
              value={storageAmount}
              disabled={mutation.isPending || !!initialStorage}
              error={storageAmount !== "" && invalidStorage}
              onChange={(event) => setStorageAmount(event.target.value)}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "4px 0 0 4px",
                },
              }}
              slotProps={{ htmlInput: { min: 1, step: 1 } }}
            />
            <Select
              className="w-[104px] bg-white"
              value={storageUnit}
              disabled={mutation.isPending || !!initialStorage}
              inputProps={{ "aria-label": "용량 단위" }}
              onChange={(event) => setStorageUnit(event.target.value as StorageUnit)}
              MenuProps={{ style: { zIndex: 3000 } }}
              sx={{
                marginLeft: "-1px",
                borderRadius: "0 4px 4px 0",
              }}
            >
              {STORAGE_UNITS.map((unit) => (
                <MenuItem key={unit} value={unit}>{unit}</MenuItem>
              ))}
            </Select>
          </div>
          <p className={`mt-1 text-[12px] ${storageAmount !== "" && invalidStorage ? "text-[#D32F2F]" : "text-black/60"}`}>
            {storageAmount !== "" && invalidStorage
              ? "1 이상의 정수를 입력해 주세요."
              : initialStorage ? "기존 용량명은 변경할 수 없습니다." : "숫자와 단위를 선택해 주세요."}
          </p>
        </div>
        <TextField
          label="출고가(원)*"
          type="number"
          value={price}
          disabled={mutation.isPending}
          error={price !== "" && invalidPrice}
          helperText={price !== "" && invalidPrice ? "0원 이상의 정수로 입력해 주세요." : "원 단위로 입력해 주세요."}
          onChange={(event) => setPrice(event.target.value)}
          slotProps={{
            inputLabel: { shrink: true },
            htmlInput: { min: 0, max: MAX_PRICE, step: 1 },
          }}
        />
      </div>
    </ConfirmModal>
  );
}
